import { useCanvasFileRecords } from "@/api/fileRecords";
import { buildFileRecordLookups } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/canvas/canvasRailContentsModel";
import { useFilesRailContextFileActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailContextFileActions";
import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import { useCanvasApi } from "@/pages/canvas/context/CanvasContext";
import { useCanvasNodesMirrorState } from "@/pages/canvas/context/CanvasNodesMirrorContext";
import { resolveFileRecordFromList } from "@/pages/canvas/utils/resolveFileRecordFromList";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import type { FileRecord } from "@cohesive/api-client";
import { blockDisplayFromNodeData } from "@cohesive/ui-components";
import { useCallback, useMemo } from "react";

/** Handler deps for file-record registry actions on canvas surfaces (context menu, etc.). */
export function useCanvasFileRecordMenuHandlerDeps(
  canvasId: string | undefined,
  fileRecord: FileRecord | null,
): FileRecordActionHandlerDeps | undefined {
  const { data: fileRecords = [] } = useCanvasFileRecords(canvasId);
  const { nodes: rawNodes } = useCanvasNodesMirrorState(canvasId ?? "");
  const openFileRecord = useOpenFileRecord();
  const canvas = useCanvasApi();

  const fileRecordIdToNodeId = useMemo(() => {
    if (!canvasId) {
      return new Map<string, string>();
    }
    return buildFileRecordLookups(fileRecords, rawNodes).fileRecordIdToNodeId;
  }, [canvasId, fileRecords, rawNodes]);

  const { menuActions } = useFilesRailContextFileActions({
    allRecords: fileRecords,
    fileRecordIdToNodeId,
  });

  const resolveRecord = useCallback(
    (record: FileRecord) =>
      resolveFileRecordFromList(record, fileRecords) ?? record,
    [fileRecords],
  );

  // A block's live node box, read from the node mirror this hook already
  // subscribes to. Promoting a block passes it along so the record remembers
  // the size the user last saw it at — the server never reads the room.
  const resolveBlockDisplaySize = useCallback(
    (record: FileRecord) => {
      const nodeId = fileRecordIdToNodeId.get(record.id);
      const node = nodeId ? rawNodes.find((n) => n.id === nodeId) : undefined;
      if (!node || blockDisplayFromNodeData(node.data)?.mode !== "block") {
        return undefined;
      }
      return typeof node.width === "number" &&
        typeof node.height === "number" &&
        node.width > 0 &&
        node.height > 0
        ? { width: node.width, height: node.height }
        : undefined;
    },
    [fileRecordIdToNodeId, rawNodes],
  );

  return useMemo((): FileRecordActionHandlerDeps | undefined => {
    if (!fileRecord) {
      return undefined;
    }

    return {
      onOpen: () => openFileRecord(fileRecord.id),
      onRename: menuActions.onRename
        ? (record) => menuActions.onRename!(resolveRecord(record))
        : undefined,
      onCopy: menuActions.onCopy
        ? (record) => menuActions.onCopy!(resolveRecord(record))
        : undefined,
      // A file node maps 1:1 to the file's canvas membership, so Delete is the
      // real thing — remove the node AND trash the record (menuActions.onDelete
      // does both). The non-destructive counterpart is Hide on canvas, which
      // keeps node + membership and just stops rendering it (`node.hidden`).
      onDelete: menuActions.onDelete
        ? (record) => menuActions.onDelete!(resolveRecord(record))
        : undefined,
      onHideOnCanvas: (record) => {
        const nodeId = fileRecordIdToNodeId.get(record.id);
        if (nodeId) canvas.setNodeHidden(nodeId, true);
      },
      onShowOnCanvas: (record) => {
        const nodeId = fileRecordIdToNodeId.get(record.id);
        if (nodeId) canvas.setNodeHidden(nodeId, false);
      },
      resolveBlockDisplaySize,
    };
  }, [
    canvas,
    fileRecord,
    fileRecordIdToNodeId,
    menuActions,
    openFileRecord,
    resolveRecord,
    resolveBlockDisplaySize,
  ]);
}
